// Recost alert feed (F6c): supplier price moves that changed dish cost, with the GP
// impact per affected dish, and acknowledge.
//   GET  /api/recost-alerts?status=open|acknowledged|all
//   POST /api/recost-alerts/{id}/acknowledge
// Figures arrive as exact decimal strings; they are only rounded for display, and the
// exact value is kept in the cell title.
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Loader2, RefreshCw } from 'lucide-react';
import { apiBase, authHeaders } from '../utils/authHeaders';
import { apiErrorMessage } from '../utils/apiError';
import { fmtUnitCost } from '../utils/unitCost';
import { formatMoney } from '../utils/money';

const num = v => (v === null || v === undefined || v === '' ? null : Number(v));
export const fmtPctPoints = (v, digits = 1) => {
  const n = num(v);
  return n === null || Number.isNaN(n) ? '—' : `${(n * 100).toFixed(digits)}%`;
};
export const fmtSignedPct = v => {
  const n = num(v);
  return n === null || Number.isNaN(n) ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
};
const fmtGbp = v => {
  const n = num(v);
  return n === null || Number.isNaN(n) ? '—' : formatMoney(n);
};

export default function RecostAlerts() {
  const [status, setStatus] = useState('open');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${apiBase()}/api/recost-alerts?status=${status}`, { headers: authHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiErrorMessage(body.detail, res.statusText || 'Could not load alerts.'));
      setData(body);
    } catch (e) { setData(null); setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const acknowledge = async id => {
    setBusy(id); setError('');
    try {
      const res = await fetch(`${apiBase()}/api/recost-alerts/${encodeURIComponent(id)}/acknowledge`, {
        method: 'POST', headers: authHeaders({ json: true }), body: JSON.stringify({}) });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(apiErrorMessage(body.detail, 'Could not acknowledge the alert.'));
      }
      await load();
    } catch (e) { setError(e.message || String(e)); }
    finally { setBusy(''); }
  };

  const alerts = data?.alerts || [];
  return (
    <section className="space-y-4" data-testid="recost-alerts" aria-label="Recost alerts">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-ink">Recost alerts</h2>
          <p className="text-sm text-ink-muted">Supplier price moves that changed what your dishes cost.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-ink-muted">Show{' '}
            <select aria-label="Alert status" value={status} onChange={e => setStatus(e.target.value)}
              className="ml-1 px-2 py-1 border border-surface-border rounded-md bg-surface">
              <option value="open">Open</option><option value="acknowledged">Acknowledged</option><option value="all">All</option>
            </select>
          </label>
          <button onClick={load} disabled={loading} aria-label="Refresh alerts"
            className="px-3 py-1.5 border border-surface-border rounded-md text-sm"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>
      {error && <div role="alert" className="bg-danger/5 border border-danger/30 text-danger rounded-md p-3 text-sm flex gap-2"><AlertTriangle className="w-4 h-4" />{error}</div>}
      {loading ? <div role="status" className="p-8 text-center text-ink-muted"><Loader2 className="w-5 h-5 mx-auto animate-spin" /> Loading alerts…</div>
        : !alerts.length ? <p className="p-8 text-center text-sm text-ink-muted" data-testid="recost-alerts-empty">No {status === 'all' ? '' : status} recost alerts.</p>
          : alerts.map(alert => (
            <article key={alert.id} className="bg-surface border border-surface-border rounded-lg p-4" data-testid={`recost-alert-${alert.id}`}>
              <header className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="font-medium text-ink">{alert.item_name || alert.item_id}</span>{' '}
                  <span className="font-data" title={`${alert.old_price} → ${alert.new_price}`}>
                    {fmtUnitCost(alert.old_price, alert.unit)} → {fmtUnitCost(alert.new_price, alert.unit)}
                  </span>{' '}
                  <span className={alert.direction === 'up' ? 'text-danger' : 'text-ok'} data-testid="recost-alert-pct">{fmtSignedPct(alert.pct_change)}</span>
                </div>
                {alert.acknowledged
                  ? <span className="text-xs text-ink-muted">Acknowledged {alert.acknowledged_at?.slice(0, 10)}</span>
                  : <button onClick={() => acknowledge(alert.id)} disabled={busy === alert.id}
                      className="px-3 py-1.5 bg-primary-500 text-surface rounded-md text-sm"><Check className="w-4 h-4 inline mr-1" />Acknowledge</button>}
              </header>
              {(alert.affected_dishes || []).length ? (
                <table className="w-full text-sm mt-3">
                  <thead className="text-xs text-ink-muted uppercase"><tr>
                    <th className="text-left py-1">Dish</th><th className="text-right">Cost/portion</th><th className="text-right">Change</th>
                    <th className="text-left pl-4">Menu item</th><th className="text-right">GP before</th><th className="text-right">GP after</th><th className="text-right">GP change</th>
                  </tr></thead>
                  <tbody>{alert.affected_dishes.flatMap(dish => (dish.menu_items.length ? dish.menu_items : [null]).map((m, i) => (
                    <tr key={`${dish.recipe_id}-${i}`} className="border-t border-surface-border">
                      <td className="py-1">{i === 0 ? dish.name || dish.recipe_id : ''}</td>
                      <td className="text-right font-data" title={dish.new_cost_per_portion || ''}>{i === 0 ? `${fmtGbp(dish.old_cost_per_portion)} → ${fmtGbp(dish.new_cost_per_portion)}` : ''}</td>
                      <td className="text-right font-data">{i === 0 ? fmtGbp(dish.cost_change_per_portion) : ''}</td>
                      <td className="pl-4">{m ? m.name : 'Not on the menu'}</td>
                      <td className="text-right font-data" title={m?.old_gp_pct || ''}>{m ? fmtPctPoints(m.old_gp_pct) : '—'}</td>
                      <td className="text-right font-data" title={m?.new_gp_pct || ''}>{m ? fmtPctPoints(m.new_gp_pct) : '—'}</td>
                      <td className="text-right font-data" data-testid="recost-alert-gp-change">{m ? fmtPctPoints(m.gp_change) : '—'}</td>
                    </tr>)))}</tbody>
                </table>
              ) : <p className="text-xs text-ink-muted mt-2">No costed dishes are affected.</p>}
            </article>))}
    </section>
  );
}
